import { useState } from 'react';
import { Search, AlertTriangle, CheckCircle, X } from 'lucide-react';
import { borerDamageImages } from '../data/mock';

export const BorerIdentificationSection = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <section 
      id="identify-damage" 
      className="section-dark wood-bg wood-grain"
      style={{ position: 'relative' }}
    >
      <div className="container relative" style={{ zIndex: 2 }}>
        {/* Section Header */}
        <div className="text-center mb-12">
          <span 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 font-display text-sm uppercase tracking-widest"
            style={{ 
              background: 'rgba(212, 168, 75, 0.15)',
              border: '1px solid var(--border-gold)',
              color: 'var(--gold-primary)'
            }}
          >
            <Search size={16} />
            Identify the Problem
          </span>
          <h2 className="heading-1 text-gold mb-4">
            Is It Really Borer Damage?
          </h2>
          <p 
            className="body-large max-w-2xl mx-auto mb-6"
            style={{ color: 'var(--text-muted)' }}
          >
            Before you call, check if your furniture shows these telltale signs of wood borer infestation. 
            <span style={{ color: 'var(--gold-primary)' }}> Don't confuse dust or debris with actual borer damage.</span>
          </p>
        </div>

        {/* Warning Box */}
        <div 
          className="max-w-3xl mx-auto mb-10 p-4 rounded-lg flex items-start gap-3"
          style={{ 
            background: 'rgba(212, 168, 75, 0.1)',
            border: '1px solid var(--border-gold)'
          }}
        >
          <AlertTriangle size={24} style={{ color: 'var(--gold-primary)', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <p className="body-medium mb-2" style={{ color: 'var(--text-light)' }}>
              <strong>Key Sign:</strong> Look for fine, powdery wood dust (called "frass") near or below your furniture.
            </p>
            <p className="body-small" style={{ color: 'var(--text-muted)' }}>
              This sawdust-like material is pushed out by borers as they tunnel through wood. Fresh frass indicates active infestation.
            </p>
          </div>
        </div>

        {/* Image Gallery */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {borerDamageImages.map((image, index) => (
            <div 
              key={index}
              className="card-wood cursor-pointer group"
              style={{ padding: '0.5rem' }}
              onClick={() => setSelectedImage(image)}
            >
              <div 
                className="aspect-square rounded-lg overflow-hidden mb-3"
                style={{ background: 'var(--bg-dark)' }}
              >
                <img 
                  src={image.url}
                  alt={image.caption}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <p 
                className="body-small text-center"
                style={{ color: 'var(--text-muted)' }}
              >
                {image.caption}
              </p>
            </div>
          ))}
        </div>

        {/* Checklist */}
        <div className="max-w-2xl mx-auto">
          <h3 
            className="heading-3 text-center mb-6"
            style={{ color: 'var(--gold-primary)' }}
          >
            Signs You Have Wood Borers
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              "Fine powdery dust (frass) below furniture",
              "Small round holes (1-2mm) in wood surface",
              "Weak or crumbling wood sections",
              "Sawdust trails on shelves or floors",
              "Tiny beetles near wooden items",
              "Hollow sound when tapping wood"
            ].map((sign, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 p-3 rounded-lg"
                style={{ 
                  background: 'rgba(76, 175, 80, 0.1)',
                  border: '1px solid rgba(76, 175, 80, 0.3)'
                }}
              >
                <CheckCircle size={18} style={{ color: 'var(--green-accent)', flexShrink: 0 }} />
                <span className="body-small" style={{ color: 'var(--text-light)' }}>
                  {sign}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Image Modal */}
        {selectedImage && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0, 0, 0, 0.9)' }}
            onClick={() => setSelectedImage(null)}
          >
            <button
              className="absolute top-4 right-4 p-2 rounded-full"
              style={{ background: 'rgba(255, 255, 255, 0.1)' }}
              onClick={() => setSelectedImage(null)}
            >
              <X size={24} style={{ color: 'white' }} />
            </button>
            <div 
              className="max-w-4xl max-h-[80vh] overflow-hidden rounded-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <img 
                src={selectedImage.url}
                alt={selectedImage.caption}
                className="w-full h-full object-contain"
              />
              <p 
                className="text-center p-4 body-medium"
                style={{ color: 'var(--gold-primary)', background: 'var(--bg-dark)' }}
              >
                {selectedImage.caption}
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
